import {
  WSFilters,
  WSInfiniteScroll,
  WSList
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSession } from "@hooks/useSession";
import { useQueryWorkLogsWithPayees } from "../../../../query/workLog/queries/useQueryWorkLogsWithPayees";
import { useQueryWorkLogsSummary } from "../../../../query/workLog/queries/useQueryWorkLogsSummary";
import { WSQueries } from "../../../../query/WSQuery";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { useInvoicesMap } from "../../hooks/useInvoicesMap";
import { usePayersMap } from "../../hooks/usePayersMap";
import { routeHelpersWorkLogPayeeDetails } from "../../paths";
import {
  WorkLogsFilterEmptyState,
  WorkLogsPayeeEmptyState
} from "../WorkLogsEmptyStates";
import { useBuildConfig } from "./filters/config";
import { createListRequest } from "./filters/request";
import { Filter, WorkLogPayeeTabFilterStatus } from "./filters/types";
import { TableWorkLogsPayeeView } from "./TableWorkLogsPayeeView";

type Props = {
  status?: Filter["status"];
};

export const FilterableTableWorkLogsPayeeView: React.FC<Props> = props => {
  const { accountId } = useSession();
  const history = useHistory();

  const { urlQuery, setUrlQuery } = useUrlQuery<Filter>();

  const request = useMemo(
    () =>
      createListRequest(accountId, {
        status: props.status,
        ...urlQuery.filter
      }),
    [accountId, props.status, urlQuery.filter]
  );

  const filtersConfig = useBuildConfig();

  const queryWorkLogs = useQueryWorkLogsWithPayees(request);
  const queryWorkLogsSummary = useQueryWorkLogsSummary(request);

  const payersMap = usePayersMap(queryWorkLogs.data);
  const { invoicesMap } = useInvoicesMap(queryWorkLogs.data);

  const hasFilters = useMemo(
    () => Object.keys(urlQuery.filter || {}).length > 0,
    [urlQuery.filter]
  );

  return (
    <WSList gap="2XL">
      <WSFilters<Filter>
        values={urlQuery.filter}
        onFilter={newValue => {
          setUrlQuery({ filter: newValue });
        }}
        primaryFilters={filtersConfig}
        info={{
          count: queryWorkLogsSummary.data?.listSize || 0,
          amount: queryWorkLogsSummary.data?.totalAmount
        }}
      />

      <WSQueries queries={{ queryWorkLogs }}>
        {({ queryWorkLogsData }) =>
          hasFilters && queryWorkLogsData.length === 0 ? (
            <WorkLogsFilterEmptyState />
          ) : queryWorkLogsData.length === 0 ? (
            <WorkLogsPayeeEmptyState
              status={props.status as WorkLogPayeeTabFilterStatus}
            />
          ) : (
            <WSInfiniteScroll
              mt="M"
              onLoad={() => queryWorkLogs.fetchNextPage()}
              loadMore={queryWorkLogsData.length > 0}
              endOfList={!queryWorkLogs.hasNextPage}
              loading={!!queryWorkLogs.isFetchingNextPage}
            >
              <TableWorkLogsPayeeView
                workLogs={queryWorkLogsData}
                payersMap={payersMap}
                invoicesMap={invoicesMap}
                onRowClick={workLog => {
                  history.push(
                    routeHelpersWorkLogPayeeDetails.createLocationObject({
                      workLogId: workLog.id!
                    })
                  );
                }}
              />
            </WSInfiniteScroll>
          )
        }
      </WSQueries>
    </WSList>
  );
};
